export enum CitationType {
  SingleCite = 0,
  ParallelCites = 1,
}

/**
 * Represents a single citation reference to a case
 * (e.g., one reporter entry with its identifier, court, hyperlink, etc.)
 */
export interface CaseReference {
  docId: string;
  title: string;
  identifier: string;
  court: string;
  courtAbbreviation: string;
  docDate: string;
  hyperlink: string;
}

/**
 * Represents a law case that may be cited through one or more citation references.
 * For SingleCite, `references` contains a single CaseReference.
 * For ParallelCites, `references` contains multiple CaseReferences pointing to the same case
 * across different reporters.
 */
export interface LawCase {
  identifierOnDocument: string;
  titleOnDocument?: string;
  status: ResolutionStatus;
  foundDocs: CaseReference[];
}

export type ResolvedLawCase = LawCase;

export interface AnalyzeResult {
  documentReferenceId?: string;
  citations: LawCase[];
  citationType: CitationType;
  lastUpdated?: string;
  resolvedLawCase?: ResolvedLawCase | "none";
}

export enum ResolutionStatus {
  NotFound = 0, // Not Found - Citation is not found.
  ValidatedAndCertified = 1, // Validated and Certified - Citation has been validated.
  DuplicateResolved = 2, // Duplicate Resolved - Citation has been validated.
  NeedsVerification = 3, // Duplicate Resolved - Citation requires user verification.
  Selected = 4, // Validated and Certified - Citation has been validated.
  UserResolvedHyperlink = 5, // User Resolved Hyperlink - Citation has been validated.
  Ignored = 6, // Unmatched Non Certified - Citation is ignored/not applicable.
  RequiresManualReview = 7, // Requires Manual Review - Citation needs manual intervention.
  UserAddedHyperlink = 8, // User Added Hyperlink - Citation has been validated.
  Removed = 9, // User Added Hyperlink - Citation has been validated.
}

export enum ResolutionStatusDisplayText {
  NotFound = "Not Found",
  ValidatedAndCertified = "Validated and Certified",
  DuplicateResolved = "Duplicate Resolved",
  NeedsVerification = "Needs Verification",
  Selected = "Selected",
  UserResolvedHyperlink = "User Resolved Hyperlink",
  Ignored = "Ignored",
  RequiresManualReview = "Requires Manual Review",
  UserAddedHyperlink = "User Added Hyperlink",
  Removed = "Removed",
}

export interface AnalyzeCitationEntry {
  identifierOnDocument: string;
  titleOnDocument?: string;
  yearOnDocument?: string;
}

export interface AnalyzeCitationItem {
  documentReferenceId?: string;
  citations: AnalyzeCitationEntry[];
  citationType: CitationType;
}

export interface AnalyzeLawCasesResponse {
  results: AnalyzeResult[];
}

export interface AnalyzeResultsStore {
  version: string;
  lastUpdated: string;
  results: AnalyzeResult[];
  fuzzySearchCompleted?: boolean; // Flag to track if fuzzy search has been performed
}

export interface DocumentDetailResponse {
  id: string;
  brs_key: number;
  brs_small_summary: string;
  brs_title: string;
  DocketNumber: string;
  Identifiers: string;
  Date: string;
  Court: string;
  State: string;
  Summary: string;
  Html: string;
  _version_: number;
  score: number | null;
}

/**
 * Scanning pipeline status. Flows:
 *   Idle → Scanning → ExactSearching → ExactComplete → FuzzySearching → Complete
 *   Any stage can transition to Error or NoCitations
 */
export enum ScanningStatus {
  Idle = "idle",
  Scanning = "scanning", // Document scan + bookmark extraction
  ExactSearching = "exact-searching", // Exact API call in progress
  ExactComplete = "exact-complete", // Exact done — user can view summary
  FuzzySearching = "fuzzy-searching", // Fuzzy API call in progress (background)
  Complete = "complete", // Everything finished
  Error = "error",
  NoCitations = "no-citations",
}
